/**
 * Coming Soon Page
 * Displays while site is in maintenance/coming soon mode
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const ComingSoonPage = ({ launchDate, tagline }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const target = launchDate ? new Date(launchDate) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000); // Default 30 days
      const now = new Date();
      const difference = target - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [launchDate]);

  const TimeBlock = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/20">
        <span className="text-3xl sm:text-4xl font-light text-white">
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="mt-2 text-xs sm:text-sm uppercase tracking-widest text-white/60">
        {label}
      </span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex flex-col items-center justify-center px-6 relative overflow-hidden" data-testid="coming-soon-page">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#2a2a2a] via-[#1a1a1a] to-[#0a0a0a]" />
      
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-brand/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

      <div className="relative z-10 text-center max-w-2xl mx-auto">
        {/* Logo */}
        <div className="mb-8">
          <Link to="/" className="inline-block">
            <h1 className="font-heading text-4xl sm:text-5xl italic text-white tracking-tight">
              The Cool-Girl Cut
            </h1>
          </Link>
        </div>

        {/* Tagline */}
        <p className="text-lg sm:text-xl text-white/70 mb-12 font-light leading-relaxed">
          {tagline || "Curated for the cool girls, for cool girls. A vibe, not a style — it's how you feel."}
        </p>

        {/* Coming Soon Text */}
        <h2 className="text-sm uppercase tracking-[0.3em] text-brand mb-8 font-mono">
          Coming Soon
        </h2>

        {/* Countdown Timer */}
        <div className="flex items-center justify-center gap-3 sm:gap-6 mb-12">
          <TimeBlock value={timeLeft.days} label="Days" />
          <span className="text-2xl text-white/30 mt-[-24px]">:</span>
          <TimeBlock value={timeLeft.hours} label="Hours" />
          <span className="text-2xl text-white/30 mt-[-24px]">:</span>
          <TimeBlock value={timeLeft.minutes} label="Minutes" />
          <span className="text-2xl text-white/30 mt-[-24px]">:</span>
          <TimeBlock value={timeLeft.seconds} label="Seconds" />
        </div>

        {/* Subtle footer */}
        <p className="text-xs text-white/30 uppercase tracking-widest">
          Something beautiful is on its way
        </p>
      </div>

      {/* Hidden admin access hint - only visible on hover at bottom */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 opacity-0 hover:opacity-100 transition-opacity">
        <Link 
          to="/admin" 
          className="text-xs text-white/20 hover:text-white/40 transition-colors"
        >
          Admin
        </Link>
      </div>
    </div>
  );
};

export default ComingSoonPage;
